import type { ReactElement } from 'react';
import { Input } from '../../components/forms/Input';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { AvailableDataSource } from './DynamicFieldValue';
import { DynamicFieldValue } from './DynamicFieldValue';

/**
 * Configuration form for the `product_match` step.
 *
 * The step takes the product lines an earlier step extracted, scores them
 * against the item catalog, and queues the results for human review. The form
 * is deliberately small: where the lines come from, who the request belongs to,
 * and how many candidates to keep per line.
 */

export interface ProductMatchStepConfigProps {
	config: {
		lines?: string;
		emailRecordId?: string;
		clientId?: string;
		requestName?: string;
		category?: string;
		topN?: number;
	};
	setConfig: (config: ProductMatchStepConfigProps['config']) => void;
	getAvailableDataSources?: () => Array<AvailableDataSource>;
	getDisplayText?: (value: string) => string;
}

/**
 * Mirrors the server-side clamp on the step. Keeping the bounds here as well
 * means a stored config never carries a value the step would silently change.
 */
const DEFAULT_TOP_N = 5;
const MIN_TOP_N = 1;
const MAX_TOP_N = 10;

export function ProductMatchStepConfig({
	config,
	setConfig,
	getAvailableDataSources,
	getDisplayText,
}: ProductMatchStepConfigProps): ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const updateConfig = <K extends keyof ProductMatchStepConfigProps['config']>(
		key: K,
		value: ProductMatchStepConfigProps['config'][K],
	): void => {
		setConfig({ ...config, [key]: value });
	};

	/** Clearing the field falls back to the server default rather than zero. */
	const updateTopN = (raw: string): void => {
		const parsed = parseInt(raw, 10);

		if (isNaN(parsed)) {
			updateConfig('topN', undefined);
			return;
		}

		updateConfig('topN', Math.min(MAX_TOP_N, Math.max(MIN_TOP_N, parsed)));
	};

	return (
		<div className="space-y-6">
			<div>
				<h3
					className={`text-base font-semibold mb-4 ${colors.classes.textPrimary}`}
				>
					{t('Product Match')}
				</h3>

				{/* Lines to match. Held as a full-value variable reference so the step
				    reads the resolved array rather than a stringified copy of it. */}
				<div className="mb-4">
					<DynamicFieldValue
						label={t('Product Lines')}
						value={config.lines ?? ''}
						onChange={(value) => {
							updateConfig('lines', value);
						}}
						dataType="string"
						required={true}
						placeholder={t('Select the product lines to match')}
						description={t(
							'The extracted product lines from an earlier step, such as the order sheet lines produced by Read Email.',
						)}
						availableData={getAvailableDataSources?.() ?? []}
						displayTransform={getDisplayText}
					/>
				</div>

				{/* Email provenance */}
				<div className="mb-4">
					<DynamicFieldValue
						label={t('Email Record ID')}
						value={config.emailRecordId ?? ''}
						onChange={(value) => {
							updateConfig('emailRecordId', value);
						}}
						dataType="string"
						placeholder={t('Select the Email Record ID')}
						description={t(
							'Optional. Used to link the request to the email and resolve the client from the sender.',
						)}
						availableData={getAvailableDataSources?.() ?? []}
						displayTransform={getDisplayText}
					/>
				</div>

				{/* Explicit client */}
				<div className="mb-4">
					<DynamicFieldValue
						label={t('Client')}
						value={config.clientId ?? ''}
						onChange={(value) => {
							updateConfig('clientId', value);
						}}
						dataType="string"
						placeholder={t('Select the Client ID')}
						description={t(
							'Optional. Sets the client directly instead of resolving it from the email sender.',
						)}
						availableData={getAvailableDataSources?.() ?? []}
						displayTransform={getDisplayText}
					/>
				</div>

				{/* Request name */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Request Name')}
					</label>
					<Input
						value={config.requestName ?? ''}
						onChange={(e) => {
							updateConfig(
								'requestName',
								e.target.value === '' ? undefined : e.target.value,
							);
						}}
						placeholder={t('For example: Reorder request')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t(
							'Optional. Names the entry in the review queue. Defaults to the email subject.',
						)}
					</p>
				</div>

				{/* Category */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Category')}
					</label>
					<Input
						value={config.category ?? ''}
						onChange={(e) => {
							updateConfig(
								'category',
								e.target.value === '' ? undefined : e.target.value,
							);
						}}
						placeholder={t('For example: Gloves')}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('Optional. Limits matching to a single catalog category.')}
					</p>
				</div>

				{/* Candidates kept per line */}
				<div className="mb-4">
					<label
						className={`text-sm font-medium mb-2 block ${colors.classes.textPrimary}`}
					>
						{t('Candidates Per Line')}
					</label>
					<Input
						type="number"
						value={config.topN ?? DEFAULT_TOP_N}
						onChange={(e) => {
							updateTopN(e.target.value);
						}}
						min={MIN_TOP_N}
						max={MAX_TOP_N}
					/>
					<p className={`text-xs mt-1 ${colors.classes.textSecondary}`}>
						{t('How many candidate items to keep for each line. Default 5.')}
					</p>
				</div>
			</div>
		</div>
	);
}
